import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableSortOrder, VirtualConnectionTableUpstreamTablesQueryVariables, VirtualConnectionTableUpstreamTablesQuery, TablesDisplayGridRowDataFragment, VirtualConnectionTableUpstreamTablesFilteredByColumnQueryVariables, VirtualConnectionTableUpstreamTablesFilteredByColumnQuery , VirtualConnectionTableUpstreamTablesFilteredByColumnDocument , VirtualConnectionTableUpstreamTablesDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';


type QueryVariables = VirtualConnectionTableUpstreamTablesQueryVariables;
type QueryResults = VirtualConnectionTableUpstreamTablesQuery;
type QueryResultListNode = TablesDisplayGridRowDataFragment;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableUpstreamTablesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableUpstreamTablesFilteredByColumnQuery;
type QueryResultListNodeWithFilter = TablesDisplayGridRowDataFragment;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0] &&
    resultData.virtualConnectionTables[0].upstreamTablesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.upstreamTablesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column;
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type VirtualConnectionTableUpstreamTablesQueryGridProps = GridSortProps<TablesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredColumnId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionTableUpstreamTablesQueryGrid(props: VirtualConnectionTableUpstreamTablesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredTablesLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Table as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamTablesFilteredByColumnDocument}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableUpstreamTablesDocument}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
